import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from './ui/card';
import { GripVertical } from 'lucide-react';
import { useIsReadOnly } from '../contexts/ReadOnlyContext';
import { type PipelineNodeField } from '../services/pipelineNode.service';

interface SortableColumnItemProps {
  field: PipelineNodeField;
}

function SortableColumnItem({ field }: SortableColumnItemProps) {
  const readOnly = useIsReadOnly();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || field.name || '', disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="p-3"
    >
      <div className="flex items-center gap-3">
        {!readOnly && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing flex-shrink-0"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate">
            {field.name || field.label || 'Unnamed Column'}
          </span>
          {field.type && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {field.type}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

interface SourceColumnsReorderListProps {
  fields: PipelineNodeField[];
  onFieldsChange: (fields: PipelineNodeField[]) => void;
}

export function SourceColumnsReorderList({
  fields,
  onFieldsChange,
}: SourceColumnsReorderListProps) {
  const readOnly = useIsReadOnly();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (readOnly || !over || active.id === over.id) return;

    const oldIndex = fields.findIndex(
      (f) => (f.id || f.name) === active.id
    );
    const newIndex = fields.findIndex(
      (f) => (f.id || f.name) === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const newFields = arrayMove(fields, oldIndex, newIndex);
      onFieldsChange(newFields);
    }
  };

  if (!fields || fields.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No columns defined</p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={fields.map((f) => f.id || f.name || '')}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {fields.map((field) => (
            <SortableColumnItem
              key={field.id || field.name}
              field={field}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
